import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel,
  Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fashionInformesService } from '../../../services/fashionService';
import { CurvaTallesInformeRow } from '../../../types/fashion';

const CurvaTallesInforme: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<CurvaTallesInformeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [codigoModelo, setCodigoModelo] = useState('');
  const [codigoTemporada, setCodigoTemporada] = useState('');
  const [codigoColor, setCodigoColor] = useState('');
  const [soloQuiebre, setSoloQuiebre] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fashionInformesService.getCurvaTalles({
        codigoModelo: codigoModelo || undefined,
        codigoTemporada: codigoTemporada || undefined,
        codigoColor: codigoColor || undefined,
        soloQuiebre,
      });
      setRows(res.data || []);
    } catch {
      enqueueSnackbar('Error al obtener el informe', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [codigoModelo, codigoTemporada, codigoColor, soloQuiebre, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/inventario/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Curva de talles
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Stock por modelo, color y talle. Las filas en quiebre se destacan.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Modelo"
                  fullWidth
                  value={codigoModelo}
                  onChange={(e) => setCodigoModelo(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Temporada"
                  fullWidth
                  value={codigoTemporada}
                  onChange={(e) => setCodigoTemporada(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Color"
                  fullWidth
                  value={codigoColor}
                  onChange={(e) => setCodigoColor(e.target.value.toUpperCase())}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={<Checkbox checked={soloQuiebre} onChange={(e) => setSoloQuiebre(e.target.checked)} />}
                  label="Solo quiebre"
                />
              </Grid>
            </Grid>
            <Button variant="contained" onClick={fetchData} sx={{ alignSelf: 'flex-start' }}>
              Actualizar
            </Button>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Modelo</TableCell>
                      <TableCell>Temporada</TableCell>
                      <TableCell>Color</TableCell>
                      <TableCell>Talle</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Mínimo</TableCell>
                      <TableCell>Quiebre</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow
                        key={`${r.codigoArticulo}-${r.codigoTalle}`}
                        sx={{ bgcolor: r.quiebre ? '#ffebee' : undefined }}
                      >
                        <TableCell>{r.codigoModelo} - {r.descripcionModelo}</TableCell>
                        <TableCell>{r.nombreTemporada || r.codigoTemporada || '-'}</TableCell>
                        <TableCell>{r.nombreColor}</TableCell>
                        <TableCell>{r.codigoTalle}</TableCell>
                        <TableCell>{r.codigoArticulo}</TableCell>
                        <TableCell align="right">{r.cantidad}</TableCell>
                        <TableCell align="right">{r.stockMin ?? '-'}</TableCell>
                        <TableCell>{r.quiebre ? 'Sí' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={8} align="center">Sin datos</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CurvaTallesInforme;
